import React, { useEffect, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DbService from "../services/DbService";
import { Chip } from "@mui/material";
import ActionsCell from "./actionsCell";
import { enqueueSnackbar } from "notistack";
import {
  createPlacementQuestion,
  deletePlacementQuestion,
  updatePlacementQuestion,
} from "../services/placementQuestionService";
import { PlacementQuestion } from "../types/PlacementQuestion";
import { useAdmin } from "../context/AdminContext";
import { PlacementQuestionForm } from "./forms/PlacementQuestionForm";
import { DynamicTableListTemplate } from "./shared/DynamicTableListTemplate";
import { useHandleError } from "../hooks/useHandleError";
import sanitizeHtml from "sanitize-html";

const PlacementQuestionsList: React.FC = () => {
  const handleError = useHandleError();

  const [loading, setLoading] = useState<boolean>(true);

  const { placementQuestions, setPlacementQuestions } = useAdmin();

  const [selectedPlacementQuestion, setSelectedPlacementQuestion] = useState<
    PlacementQuestion | undefined
  >(undefined);

  const [placementQuestionToDelete, setPlacementQuestionToDelete] =
    useState<PlacementQuestion | null>(null);

  const hasFetchedRef = React.useRef(false);

  const columns: GridColDef[] = [
    {
      field: "difficulty",
      headerName: "Difficulty",
      width: 130,
      renderCell: (params) => {
        const color =
          params.value === "Easy"
            ? "primary"
            : params.value === "Hard"
            ? "error"
            : "default";
        return <Chip label={params.value || "N/A"} color={color} />;
      },
    },
    {
      field: "question",
      headerName: "Question",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        // Safely sanitize the HTML content before rendering
        const safeHtml = sanitizeHtml(params.value);
        return <div dangerouslySetInnerHTML={{ __html: safeHtml }} />;
      },
    },
    {
      field: "choices",
      headerName: "Choices",
      width: 250,
      valueGetter: (params) => {
        return params.value.join(", ");
      },
    },
    { field: "answer", headerName: "Answer", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row: PlacementQuestion = params.row as PlacementQuestion;
        return (
          <ActionsCell
            onEdit={() => handleEditPlacementQuestion(row)}
            onDelete={() => handleDeletePlacementQuestion(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchPlacementQuestions = async () => {
      setLoading(true);
      try {
        const response = await DbService.getPlacementQuestions();

        setPlacementQuestions(response.data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetchedRef.current) {
      fetchPlacementQuestions();
      hasFetchedRef.current = true;
    }
  }, []);

  const handleCloseDialog = () => {
    setSelectedPlacementQuestion(undefined);
  };

  const handleEditPlacementQuestion = (row: PlacementQuestion) => {
    setSelectedPlacementQuestion(row);
  };

  const handleDeletePlacementQuestion = async (row: PlacementQuestion) => {
    if (!row?._id) {
      enqueueSnackbar(`Id is missing from this row.`, {
        variant: "error",
      });
      return;
    }
    setPlacementQuestionToDelete(row);
  };

  const handleDeletePlacementQuestionConfirm = async (
    row: PlacementQuestion | null | undefined
  ) => {
    if (row?._id) {
      try {
        const response = await deletePlacementQuestion(row);
        enqueueSnackbar(response.message, {
          variant: "success",
        });
      } catch (error) {
        handleError(error);
      } finally {
        setPlacementQuestionToDelete(null);
      }
    }
  };

  const handlePlacementQuestionSubmit = async (
    placementQuestionData: PlacementQuestion
  ) => {
    console.log(placementQuestionData);
    if (placementQuestionData._id) {
      try {
        const response = await updatePlacementQuestion(
          placementQuestionData._id,
          placementQuestionData
        );

        enqueueSnackbar(response.message, {
          variant: "success",
        });

        setSelectedPlacementQuestion(response.data);
      } catch (error) {
        handleError(error);
      }
    } else {
      try {
        const response = await createPlacementQuestion(placementQuestionData);

        enqueueSnackbar(response.message, {
          variant: "success",
        });
        setSelectedPlacementQuestion(response.data);
      } catch (error) {
        handleError(error);
      }
    }
  };

  return (
    <DynamicTableListTemplate<PlacementQuestion>
      columns={columns}
      data={placementQuestions}
      loading={loading}
      selectedRow={selectedPlacementQuestion}
      FormComponent={
        <PlacementQuestionForm
          selectedPlacementQuestion={selectedPlacementQuestion}
          onSubmit={handlePlacementQuestionSubmit}
        />
      }
      entityLabel="Placement Question"
      dialogTitle="Placement Question Form"
      onClearSelectedRow={handleCloseDialog}
      onConfirmDelete={handleDeletePlacementQuestionConfirm}
      entityToDelete={placementQuestionToDelete}
    />
  );
};

export default PlacementQuestionsList;
